import {
  DeleteTwoTone,
  EditTwoTone,
  PlusOutlined,
  SearchOutlined,
  SyncOutlined,
} from "@ant-design/icons";
import {
  Button,
  Select,
  Table,
  Tooltip,
  Form,
  notification,
  PageHeader,
  Row,
  Col,
  Input,
} from "antd";
import { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import IconNotFound from "../../static/icon/IconNotFound";
import DeleteAction from "../../Tools/DeleteAction";

import { useAuth } from "../../context/auth";
import { formatPriceCalculateMethod, formatPriceMethod, listPriceCalculateMethod, listPriceMethod } from "../../utils/formatter";
import IconPrice from "../../static/icon/IconPrice";

const Price = () => {
  const history = useHistory();
  const { accessToken } = useAuth();

  const [form] = Form.useForm();

  const [loading, setLoading] = useState(false);

  const [update, setUpdate] = useState(0);
  const [total, setTotal] = useState(0);
  const [prices, setPrices] = useState([]);
  const [search, setSearch] = useState({
    pageValue: history.location.pageValue ?? {},
    searchValue: history.location.searchValue ?? {},
  });

  useEffect(() => {
    form.setFieldsValue(search.searchValue);
  }, [form, search]);

  const [currentPrice, setCurrentPrice] = useState(null);

  useEffect(() => {
    async function getPrices({ pageValue, searchValue }) {
      setLoading(true);

      const url = new URL('/api/price', window.location);
      if (searchValue.desc) url.searchParams.append('desc', searchValue.desc);
      if (searchValue.method !== null) url.searchParams.append('method', searchValue.method);
      if (searchValue.calculateMethod !== null) url.searchParams.append('calculateMethod', searchValue.calculateMethod);

      if (pageValue.page) url.searchParams.append('page', pageValue.page);
      if (pageValue.size) url.searchParams.append('size', pageValue.size);
      if (pageValue.sort) url.searchParams.append('sort', pageValue.sort);
      if (pageValue.order) url.searchParams.append('order', pageValue.order);

      const res = await fetch(url, {
        headers: { 'X-ACCESS-TOKEN': accessToken },
      });
      if (res.ok) {
        const { total, prices } = await res.json();
        setTotal(total);
        setPrices(prices);
      } else {
        notification["error"]({
          message: "Something went wrong !",
          description: `Please re-login or contact web admin. (Code: ${await res.json().then(data => data.message)})`,
        });
      }

      setLoading(false);
    };

    getPrices(search);
  }, [search, accessToken, update]);

  const columns = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      sorter: () => null,
    },
    {
      title: "Description",
      dataIndex: "descEn",
      key: "descEn",
      sorter: () => null,
    },
    {
      title: "Method",
      dataIndex: "method",
      key: "method",
      sorter: () => null,
      render: (method) => formatPriceMethod(method),
    },
    {
      title: "Calculate Method",
      dataIndex: "calculateMethod",
      key: "calculateMethod",
      sorter: () => null,
      render: (calculateMethod) => formatPriceCalculateMethod(calculateMethod),
    },
    {
      title: "Baht/hr",
      dataIndex: "bahtPerHour",
      key: "bahtPerHour",
      sorter: () => null,
      render: (bahtPerHour) => bahtPerHour + ' Baht/hr',
    },
    {
      title: "Baht/kWh",
      dataIndex: "bahtPerKwh",
      key: "bahtPerKwh",
      sorter: () => null,
      render: (bahtPerKwh) => bahtPerKwh + ' Baht/kWh',
    },
    {
      title: "Action",
      key: "action",
      width: 120,
      render: (_, price) => (
        <span style={{ whiteSpace: "nowrap" }}>
          <Tooltip placement="bottom" title="View" className="mx-1">
            <EditTwoTone
              style={{
                backgroundColor: "#F3F6F9",
                padding: 6,
                borderRadius: 6,
              }}
              onClick={() => {
                history.push({
                  pathname: `/price/${price.id}`,
                  searchValue: search.searchValue,
                  pageValue: search.pageValue,
                });
              }}
              twoToneColor="#00C9D6"
            />
          </Tooltip>

          {!price.active && (
            <Tooltip placement="bottom" title="Delete" className="mx-1">
              <DeleteTwoTone
                style={{
                  backgroundColor: "#F3F6F9",
                  padding: 6,
                  borderRadius: 6,
                }}
                onClick={() => setCurrentPrice(price)}
                twoToneColor="red"
              />
            </Tooltip>
          )}
        </span>
      ),
    },
  ];

  const [downloading, setDownloading] = useState(false);
  async function downloadXlsx({ searchValue }) {
    setDownloading(true);

    const url = new URL('/api/price', window.location);
    url.searchParams.append('mode', 'xlsx');

    if (searchValue.desc) url.searchParams.append('desc', searchValue.desc);
    if (searchValue.method !== null) url.searchParams.append('method', searchValue.method);
    if (searchValue.calculateMethod !== null) url.searchParams.append('calculateMethod', searchValue.calculateMethod);

    const res = await fetch(url, {
      headers: { 'X-ACCESS-TOKEN': accessToken },
    });
    if (res.ok) {
      const data = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(data);
      a.download = `Price_${new Date().toISOString()}.xlsx`;
      a.click();
    } else {
      if (res.status === 404) {
        notification["error"]({
          message: "No price found",
        });
      } else {
        notification["error"]({
          message: "Something went wrong !",
          description: `Please re-login or contact web admin. (Code: ${await res.json().then(data => data.message)})`,
        });
      }
    }

    setDownloading(false);
  }

  return (
    <>
      <div className="flex justify-between items-center mt-2 space-x-4">
        <PageHeader
          style={{ padding: 0, width: "100%" }}
          title={
            <span className="flex items-center mt-2">
              <IconPrice /> &nbsp;&nbsp;Price
            </span>
          }
        />

        <Button
          className="mt-2 mr-3 flex items-center px-12 border-0"
          loading={downloading}
          type="primary"
          shape="round"
          style={{ background: "#1BC5BD" }}
          onClick={() => downloadXlsx(search)}
        >
          Export
        </Button>

        <Button
          className="mt-2 flex items-center"
          icon={<PlusOutlined />}
          type="primary"
          shape="round"
          onClick={() => {
            history.push({
              pathname: `/price/new`,
              searchValue: search.searchValue,
              pageValue: search.pageValue,
            });
          }}
        >
          New Price
        </Button>
      </div>
      <div className="content">
        <div className="">
          <Form
            form={form}
            layout="vertical"
            onFinish={(values) => setSearch(search => ({
              pageValue: { ...search.pageValue, page: 1 },
              searchValue: values,
            }))}
            onReset={() => form.submit()}
          >
            <Row align="middle" gutter={16}>
              <Col className="gutter-row" span={4}>
                <Form.Item
                  label="Description"
                  name="desc"
                  colon={false}
                  initialValue={''}
                >
                  <Input
                    placeholder="Description"
                    allowClear
                  />
                </Form.Item>
              </Col>
              <Col className="gutter-row" span={4}>
                <Form.Item
                  label="Method"
                  name="method"
                  colon={false}
                  initialValue={null}
                >
                  <Select
                    showSearch
                    allowClear
                    options={[
                      { value: null, label: 'Any Method' },
                      ...listPriceMethod.map(type => ({
                        value: type.name,
                        label: type.value,
                      })),
                    ]}
                  />
                </Form.Item>
              </Col>
              <Col className="gutter-row" span={4}>
                <Form.Item
                  label="Calculate Method"
                  name="calculateMethod"
                  colon={false}
                  initialValue={null}
                >
                  <Select
                    showSearch
                    allowClear
                    options={[
                      { value: null, label: 'Any Calculate Method' },
                      ...listPriceCalculateMethod.map(type => ({
                        value: type.name,
                        label: type.value,
                      })),
                    ]}
                  />
                </Form.Item>
              </Col>
              <Col className="gutter-row" span={14}>
                <div className="flex">
                  <Button
                    className="w-28"
                    type="primary"
                    shape="round"
                    htmlType="submit"
                    icon={<SearchOutlined />}
                  >
                    Search
                  </Button>

                  <Button
                    className="w-28 ml-2"
                    type="primary"
                    ghost
                    shape="round"
                    htmlType="reset"
                    icon={<SyncOutlined />}
                  >
                    Clear Data
                  </Button>
                </div>
              </Col>
            </Row>
          </Form>
        </div>
      </div>
      <div className="content">
        <Table
          className="inbox_table"
          locale={{ emptyText: <IconNotFound /> }}
          rowKey={(price) => price.id}
          loading={loading}
          columns={columns}
          dataSource={prices}
          pagination={{
            size: "small",
            current: search.pageValue.page,
            pageSize: search.pageValue.size,
            defaultPageSize: 10,
            total,
            showQuickJumper: true,
            showSizeChanger: true,
            showTotal: (total, range) => ` ${range[0]}-${range[1]} of ${total} `,
          }}
          onChange={(pagination, _, sorter) => setSearch(search => ({
            pageValue: {
              page: pagination.current ?? 1,
              size: pagination.pageSize ?? 10,
              sort: sorter.columnKey,
              order: sorter.order === 'ascend' ? 'asc' : 'desc',
            },
            searchValue: search.searchValue,
          }))}
        />
      </div>

      <DeleteAction
        openModalActionDelete={!!currentPrice}
        name="Price"
        type="price"
        editRecord={currentPrice}
        onCancel={() => setCurrentPrice(null)}
        onOk={() => {
          setCurrentPrice(null);
          setUpdate(update + 1);
        }}
      />
    </>
  );
};

export default Price;
